import { useState, useEffect, useCallback } from 'react';

export type CookieConsentType = 'all' | 'necessary' | 'custom' | null;

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'msp_vergelijker_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'msp_vergelijker_cookie_preferences';

const defaultPreferences: CookiePreferences = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
};

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsentType>(null);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const storedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (storedConsent) {
      setConsent(storedConsent as CookieConsentType);
      if (storedPreferences) {
        try {
          setPreferences(JSON.parse(storedPreferences));
        } catch {
          setPreferences(defaultPreferences);
        }
      }
      setShowBanner(false);
    } else {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setConsent('all');
    setPreferences(allAccepted);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'all');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const acceptNecessary = useCallback(() => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setConsent('necessary');
    setPreferences(necessaryOnly);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'necessary');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(necessaryOnly));
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const saveCustomPreferences = useCallback((customPrefs: Partial<CookiePreferences>) => {
    const newPreferences: CookiePreferences = {
      necessary: true, // Always true
      analytics: customPrefs.analytics ?? false,
      marketing: customPrefs.marketing ?? false,
    };
    setConsent('custom');
    setPreferences(newPreferences);
    localStorage.setItem(COOKIE_CONSENT_KEY, 'custom');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPreferences));
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    setConsent(null);
    setPreferences(defaultPreferences);
    setShowBanner(true);
  }, []);

  return {
    consent,
    preferences,
    showBanner,
    showSettings,
    acceptAll,
    acceptNecessary,
    saveCustomPreferences,
    openSettings,
    closeSettings,
    resetConsent,
  };
}
